import dayjs from "dayjs";

import api from "./registerApi";
import {
  RegistrationCompletedAction,
  RegistrationErrorAction,
  RegistrationIsCompleting,
  ResetChildren,
  ResetContacts,
  ResetState,
  SaveChildAction,
  SaveContactAction,
  SetPossibleDuplicatedAccountAction,
  SetRequirePaymentConfiguration,
} from "./registerStateActions";
import history from "../../../routes/History";
import { NewPaymentMethod } from "../../../types/models";
import { Contact, IChild, User } from "../../../types/types";
import {
  CompleteRegistrationHttpRequest,
  IRegistrationHttpResponse,
  SaveRegistrationChildHttpRequest,
  SaveRegistrationContactHttpRequest,
  SaveRegistrationContactsHttpRequest,
} from "../../parentApi";

export const getRegistration = (dispatch: any, user: User | null) =>
  api.getRegistrationByUserId().then(
    (response: IRegistrationHttpResponse | null) => initializeState(dispatch, user, response),
    (error: any) => console.log(error)
  );

export const saveContacts = (
  dispatch: any,
  saveContacts: Contact[],
  removeContacts: Contact[],
  redirectOnSuccess?: string
): Promise<void> => {
  var httpContacts = new SaveRegistrationContactsHttpRequest();
  httpContacts.init({
    contacts: saveContacts.map((contact) => {
      var httpContact = new SaveRegistrationContactHttpRequest();
      httpContact.init(contact);
      return httpContact;
    }),
  });

  const removePromises = removeContacts
    .filter((contact) => (contact.uniqueId ?? "").length > 30)
    .map((contact) => api.removeContact(contact.uniqueId!));

  // Saved as one batch so the API can verify at least one primary contact atomically, instead of
  // looping per-contact client-side (see saveContacts endpoint).
  return Promise.all([api.saveContacts(httpContacts), ...removePromises]).then(() => {
    dispatch(ResetContacts(saveContacts));

    if (redirectOnSuccess) history.push(redirectOnSuccess);
  });
};

export const saveChildren = (
  dispatch: any,
  saveChildren: IChild[],
  removeChildren: IChild[],
  redirectOnSuccess?: string
) => {
  saveChildren.forEach((Child) => {
    var httpChild = new SaveRegistrationChildHttpRequest();
    httpChild.init(Child);
    api.saveChild(httpChild);
  });

  removeChildren.forEach((Child) => {
    const uniqueId = Child.uniqueId ?? "";
    if (uniqueId.length > 30) api.removeChild(uniqueId);
  });

  dispatch(ResetChildren(saveChildren));
  if (redirectOnSuccess) history.push(redirectOnSuccess);
};

function initializeState(dispatch: any, user: User | null, response: IRegistrationHttpResponse | null): any {
  var primaryContact = null;
  var existingContacts = (response?.contacts ?? []).map((contact) => contact as Contact);
  var hasPrimaryContact = existingContacts.some((contact) => contact.isPrimary);

  // Self-heal: rebuild the profile-based primary whenever none of the saved contacts is
  // flagged primary, not just when there are no contacts at all.
  if (!hasPrimaryContact) {
    if (response != null && user != null) {
      var profile = user.profile;
      var fullAuthorisation = {
        canApproveAmbulance: true,
        canManageAccount: true,
        canMakeAndChangeBookings: true,
        canPickUpChild: true,
        consentToAdministerMedicine: true,
        consentToMedicalTreatment: true,
        consentToTakeOutsidePremises: true,
        consentForTransportation: true,
      };

      // The mis-flagged primary contact was created with uniqueId === profile.sub (the standard
      // convention), so promote its real data instead of fabricating a new primary with the same
      // id, which would leave two contacts sharing the same uniqueId.
      var existingPrimaryContact = existingContacts.find((contact) => contact.uniqueId === profile.sub);
      var otherContacts = existingContacts.filter((contact) => contact.uniqueId !== profile.sub);

      primaryContact = existingPrimaryContact
        ? {
            ...existingPrimaryContact,
            isPrimary: true,
            authorisation: existingPrimaryContact.authorisation ?? fullAuthorisation,
          }
        : {
            uniqueId: profile.sub,
            email: profile.email,
            firstName: profile.given_name,
            lastName: profile.family_name,
            isPrimary: true,
            createdAt: dayjs().format(),
            avatarIndex: 1,
            authorisation: fullAuthorisation,
          };

      return dispatch(
        ResetState(
          [primaryContact, ...otherContacts],
          response?.children ?? [],
          response?.documents ?? [],
          response?.possibleDuplicatedAccount
        )
      );
    }
  }

  return dispatch(
    ResetState(
      existingContacts,
      response?.children ?? [],
      response?.documents ?? [],
      response?.possibleDuplicatedAccount ?? false
    )
  );
}

export function saveChild(dispatch: any, child: IChild) {
  if ((child?.firstName?.length ?? 0) > 0 && (child.lastName?.length ?? 0) > 0) {
    dispatch(SaveChildAction(child));
    var httpChild = new SaveRegistrationChildHttpRequest();

    httpChild.init(child);
    api.saveChild(httpChild).then(() => {
      checkRequirePaymentConfiguration(dispatch);
    });
  }
}

export const saveContact = (dispatch: any, contact: Contact): Promise<void> => {
  if ((contact?.firstName?.length ?? 0) > 0 && (contact.lastName?.length ?? 0) > 0) {
    dispatch(SaveContactAction(contact));
    var httpContact = new SaveRegistrationContactHttpRequest();
    httpContact.init(contact);
    return api.saveContact(httpContact);
  }
  return Promise.resolve();
};

export const checkPossibleDuplicatedAccount = (dispatch: any) => async () => {
  try {
    await api.getPossibleDuplicatedAccount().then((response: boolean) => {
      dispatch(SetPossibleDuplicatedAccountAction(response));
    });
  } catch (error) {
    console.log(error);
  }
};

export const checkRequirePaymentConfiguration = async (dispatch: any) => {
  try {
    await api.getRequirePaymentConfiguration().then((response: boolean) => {
      dispatch(SetRequirePaymentConfiguration(response));
    });
  } catch (error) {
    console.log(error);
  }
};

export const complete = async (dispatch: any, children: IChild[], contacts: Contact[], payment?: NewPaymentMethod) => {
  dispatch(RegistrationIsCompleting());

  // Fast-fail client-side instead of relying solely on the backend's 400 at /complete.
  if (!contacts.some((contact) => contact.isPrimary)) {
    dispatch(RegistrationErrorAction(new Error("Primary contact is missing")));
    return;
  }

  try {
    var httpCompleteRegistration = new CompleteRegistrationHttpRequest();

    contacts.forEach((contact) => {
      contact.createdAt = undefined;
    });

    children.forEach((child) => {
      child.createdAt = undefined;
    });

    httpCompleteRegistration.init({ children, contacts, payment });
    api
      .completeRegistrationByUserId(httpCompleteRegistration)
      .then(
        (response: any) => {
          dispatch(RegistrationCompletedAction());
          console.log(response);
        },
        (error: any) => {
          dispatch(RegistrationErrorAction(error));
        }
      )
      .catch((error: any) => {
        dispatch(RegistrationErrorAction(error));
      });
  } catch (error: any) {
    dispatch(RegistrationErrorAction(error));
  }
};
